import { store } from '../store.js';
import * as app from '../redux/app';

/**
 * Resolve a link title using a (iframely)[https://iframely.com/].
 * In feature server gives a new API for link title resolve, this API is also manage a cache for link title.
 * @param {String} link
 * @returns {String} resolve title based on given link.
 */
export const getTitle = async (link) => {
  try {
    const response = await getInfo(link);
    return response && response.title ? response.title : null;
  } catch (error) {
    return null;
  }
}

/**
 * Identifies information about the weblink.
 *
 * Possible values of type are: `weblink/embedly+image`, `weblink/embedly+video`, `weblink/embedly+embed`.
 * Note:: Type value is adjusted so that, it can be directly sent to the server in API request.
 *
 * ## Behaviour
 *  - When fetch is not repsonse within 10 seconds then also it will be resolved to `{type:"weblink"}`.
 *  - It will never fail. in case of any underlying failure as well, it will be resolved to `{type:"weblink"}`.
 *  - `title` is optional field in the response, it may not be availble in many cases.
 *
 * @param {String} link  A Weblink for which type & title is to be resolved.
 * @returns{Promise} Which is resolve to an Object `{title, description, thumbnail, type, content: { image, embed }}`. It will never fail. in case of any underlying
 * failure as well, it will be resolved to `{type:"weblink"}`. `title` and `content` is optional field in the response, it may
 *  not be availble in many cases.
 */
export const getInfo = async (link) => {
  let controller = new AbortController()

  //After 10 seconds fetch request is aboreted.
  let timeoutId = setTimeout(() => controller.abort(), 10000);
  let options = {
    headers: {
      "Content-Type": "application/json"
    },
    credentials: 'same-origin',
    signal: controller.signal
  };

  const config = app.selectors.config(store.getState());
  const apiKey = config.iframelyApiKey;

  try {
    let res = await fetch(`https://iframe.ly/api/oembed?url=${encodeURIComponent(link)}&api_key=${apiKey}`, options);
    let responseText;
    responseText = await res.text();
    responseText = responseText.trim();

    let responseJSON;
    responseJSON = JSON.parse(responseText);

    clearTimeout(timeoutId);
    return {
      title: responseJSON.title ? responseJSON.title : null,
      description: responseJSON.description || null,
      thumbnail: responseJSON.thumbnail_url || (responseJSON.type === 'photo' && responseJSON.url) || null,
      type: getMimeType(responseJSON, link),
      content: getOembedContent(responseJSON, link)
    }
  } catch (error) {
    clearTimeout(timeoutId);
    return { type: 'weblink', title: null };
  }
}

/**
 * Gives attachment type based on `iframely` response.
 * @param {Object} response response from `iframely`.
 */
const getMimeType = (response, link) => {
  if(!response || !response.html) {
    return "weblink";
  }

  if(link && link.startsWith('https://twitter.com')) {
    return "weblink/twitter";
  }

  if (response.type == "video") {
    return "weblink/embedly+video"
  }

  if (response.type == "rich") {
    return "weblink/embedly+embed";
  }

  if (response.type == "photo") {
    return "weblink/embedly+image";
  }

  return "weblink";
}

/**
 * @returns {Object} image and embed content based on given response.
 */
const getOembedContent = (response, link) => {
  const content = {};
  if(link && link.startsWith('https://twitter.com')) {
    return content;
  }

  if (response.html) {
    let nIframeIndex = response.html.indexOf('</iframe>');
    let nStartIframeIndex = response.html.indexOf('<iframe');
    if (nIframeIndex !== -1) {
      content.embed = response.html.substring(nStartIframeIndex, nIframeIndex + 9);
    }
  }

  if (response.thumbnail_url || (response.type === 'photo' && response.url)) {
    content.image = `<img src=${response.thumbnail_url || response.url}></img>`;
  }

  return content;
}